import type { SetCopy } from './catalog'
import type { Generated } from './data/generated-lazy'
import { PRICE_DATE, PRICE_SOURCE } from './voxel/prices'
import buyLinks from './data/buy-links.json'
import { setCode } from './series'

// The shape every set has, curated or generated, and the pure join between
// them. Split out of sets.ts so a client bundle can reach this (and
// `sets-client.ts`'s per-slug loader) without also pulling in sets.ts's
// eager, whole-catalog `GENERATED` import.

export interface PartLine {
  part: string
  color: number
  qty: number
  name: string
  bricklink: string
  rebrickable: string | null
  colorName: string
  bricklinkColor: number
  unit: number
}

export interface CostSummary {
  pieces: number
  lots: number
  price: number
  // Pieces touching nothing that holds them up, so the build falls apart.
  floating?: number
}

export interface BrickSet extends SetCopy {
  code: string
  cover: string
  model: string
  size: { studsWide: number; studsDeep: number; plates: number }
  steps: number
  notes: { step: number; note: string }[]
  before: CostSummary
  after: CostSummary
  parts: PartLine[]
  price: { total: number; date: string; source: string }
  // Public Rebrickable list with a Buy Parts tab, when one exists.
  buyUrl: string | null
  // Which baked parts file (public/bricks/parts/<key>.bin) the build loads,
  // when it isn't the set's own: generated sets share one.
  partsKey?: string
}

export function join(copy: SetCopy, data: Generated): BrickSet {
  return {
    ...copy,
    code: setCode(copy.series, copy.number),
    cover: `/images/bricks/${copy.slug}.webp`,
    model: `/bricks/models/${copy.slug}.ldr`,
    size: data.size,
    steps: data.steps,
    notes: data.notes,
    before: data.before,
    after: data.after,
    parts: data.parts as PartLine[],
    price: { total: data.after.price, date: PRICE_DATE, source: PRICE_SOURCE },
    buyUrl: (buyLinks as Record<string, string>)[copy.slug] ?? null,
  }
}

// What the collection page hands the browser for every set (built in
// app/bricks/shelf.ts): enough to draw and name its booklet, and the photo
// it opens on, to fetch ahead. The rest (the parts list above all) reaches
// the browser with the reader, so it isn't in every page's payload.
export type ShelfSet = Pick<
  BrickSet,
  'slug' | 'series' | 'name' | 'maker' | 'year' | 'cover' | 'palette' | 'code'
> & {
  pieces: number
  photo: string | null
  // The cover's colour before it loads, and the grounds of the booklet's
  // first two pages (stage/tones.ts).
  tone: string
  front: string
  paper: string
}

// BrickLink Wanted List XML (the format its upload page and Studio use).
export function wantedListXml(set: BrickSet) {
  const items = set.parts
    .map(
      (p) =>
        `  <ITEM>\n    <ITEMTYPE>P</ITEMTYPE>\n    <ITEMID>${p.bricklink}</ITEMID>\n    <COLOR>${p.bricklinkColor}</COLOR>\n    <MINQTY>${p.qty}</MINQTY>\n  </ITEM>`,
    )
    .join('\n')
  return `<?xml version="1.0" encoding="UTF-8"?>\n<INVENTORY>\n${items}\n</INVENTORY>\n`
}

export const BRICKLINK_UPLOAD_URL =
  'https://www.bricklink.com/v2/wanted/upload.page'
